import { useEffect, useReducer } from 'react';

/** Max safe delay for setTimeout (~24.8 days) */
const MAX_TIMEOUT = 2_147_483_647;

/**
 * Forces a re-render at the specified future timestamp.
 *
 * Returns a tick counter that increments on each scheduled
 * refresh. Include it in `useMemo`/`useCallback` dependency
 * arrays where time-dependent values need recalculation. For
 * non-memoized values (like inline `Date.now()` checks), the
 * re-render alone is sufficient.
 *
 * Past timestamps and null/undefined values are ignored.
 *
 * @param time Unix timestamp (ms) to trigger a re-render
 * @returns A tick counter that changes on each refresh
 *
 * @example
 * // Re-render when a meeting becomes joinable
 * const tick = useRefreshAt(startMs - 15 * 60_000);
 *
 * // Force a memoized filter to recompute
 * const active = useMemo(
 *   () => items.filter(isActive),
 *   [items, tick],
 * );
 */
export function useRefreshAt(time: number | null | undefined): number {
  const [tick, bump] = useReducer((n: number) => n + 1, 0);

  useEffect(() => {
    if (time == null || !Number.isFinite(time) || time <= Date.now()) {
      // Past, unset, or non-finite (NaN/Infinity) timestamps are ignored.
      // A NaN target would otherwise pass the bounds check and re-arm a
      // zero-delay timer indefinitely (NaN comparisons are always false).
      return;
    }

    let timer: ReturnType<typeof setTimeout>;

    // setTimeout caps delays at ~24.8 days. For targets further out
    // the timer fires early, so we re-arm until the target is actually
    // reached rather than bumping at the wrong time.
    const arm = () => {
      const remaining = time - Date.now();
      if (remaining <= 0) {
        bump();
        return;
      }
      // +50ms buffer ensures Date.now() is past the boundary when the
      // timer fires (setTimeout precision varies).
      timer = setTimeout(arm, Math.min(remaining + 50, MAX_TIMEOUT));
    };

    arm();
    return () => clearTimeout(timer);
  }, [time]);

  return tick;
}
